import React, { useState } from 'react';
import styles from '../../_css/Login.module.css';
import { deleteAuthToken, notifyError, notifySuccess, saveAuthToken } from '../../utils/helper';
import { setNewPassword } from '../../requests';
import { Lock, Eye, EyeOff } from 'lucide-react';
const SetNewPassword = ({ token }) => {
  const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/logo.png`;
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!password) {
      newErrors.password = 'Please enter your new password';
    } else if (password.length < 8) {
      newErrors.password = 'Your password must be at least 8 characters';
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      return;
    }
    saveAuthToken(JSON.stringify(token));
    setSubmitting(true);
    try {
      const response = await setNewPassword({ password });
      if (response?.status < 400) {
        deleteAuthToken();
        setPassword('');
        setConfirmPassword('');
        setErrors({});
        notifySuccess(response?.data?.message || 'Password updated successfully');

        //pause for 2 seconds
        new Promise((resolve) => setTimeout(resolve, 1000)).then(() => {
          window.location.href = '/login';
        });
      
      } else {
        notifyError(response?.data?.message || 'Something went wrong');
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Something went wrong');
    }finally{
      setSubmitting(false);
    }
  };

  return (
    <div className={`app-background w-full min-h-screen h-full flex items-center justify-center overflow-y-auto ${styles.loginPage}`}>
      <div className="app-background-surface w-full max-w-md px-3 py-4 sm:px-4 sm:py-6 md:px-6 md:py-8">
        <div className={styles.authCard}>
          <form onSubmit={handleSubmit} className={styles.authForm}>
            <div className={styles.authHeader}>
              <div className={styles.authLogoWrap}>
                <img src={LOGO_SRC} alt="Company logo" className={styles.authLogo} />
              </div>
              <p className={styles.authSubtitle}>Set your new password</p>
              <hr className={styles.authDivider} />
            </div>

            {/* New Password Input */}
            <div>
              <label htmlFor="password" className={styles.authLabel}>
                New Password
              </label>
              <div style={{ position: 'relative' }}>
                <Lock className={styles.authIcon} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, pointerEvents: 'none' }} />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors((prev) => ({ ...prev, password: '' }));
                  }}
                  placeholder="Enter your new password"
                  className={`${styles.authInput} ${styles.authInputIconBoth}`}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className={styles.authToggle}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && (
                <p className={styles.errorMessage} style={{ marginTop: 4 }}>{errors.password}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className={styles.authLabel}>
                Confirm Password
              </label>
              <div style={{ position: 'relative' }}>
                <Lock className={styles.authIcon} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, pointerEvents: 'none' }} />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword) setErrors((prev) => ({ ...prev, confirmPassword: '' }));
                  }}
                  placeholder="Re-enter your new password"
                  className={`${styles.authInput} ${styles.authInputIconBoth}`}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className={styles.authToggle}
                  aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                >
                  {showConfirmPassword ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className={styles.errorMessage} style={{ marginTop: 4 }}>{errors.confirmPassword}</p>
              )}
            </div>

            {/* Submit Button */}
            <button type="submit" className={styles.authSubmit} disabled={submitting}>
              {submitting ? 'Updating password...' : 'Set New Password'}
            </button>
          </form>
        </div>

        <p className={styles.authFooterNote}>
          Use a strong password with at least 8 characters
        </p>
      </div>
    </div>
  );
};

export default SetNewPassword;
